import type { EmbeddedNavigationPayload, EmbeddedService } from '@/types/embedding';

interface ShellLocationParts {
  pathname: string;
  search?: string;
  hash?: string;
}

const absoluteUrlPattern = /^[a-z][a-z\d+.-]*:/i;

const normalizePathname = (pathname: string) => {
  const trimmed = pathname.trim();
  if (!trimmed || trimmed === '/') {
    return '/';
  }

  return `/${trimmed.replace(/^\/+|\/+$/g, '')}`;
};

const normalizeSearch = (search?: string) => {
  if (!search) {
    return '';
  }

  return search.startsWith('?') ? search : `?${search}`;
};

const normalizeHash = (hash?: string) => {
  if (!hash) {
    return '';
  }

  return hash.startsWith('#') ? hash : `#${hash}`;
};

const getUrlOrigin = () => (typeof window === 'undefined' ? 'http://localhost' : window.location.origin);

const isAbsoluteUrl = (value: string) => absoluteUrlPattern.test(value);

const parseUrlPath = (value: string) => {
  const url = new URL(value || '/', getUrlOrigin());
  return {
    origin: url.origin,
    pathname: normalizePathname(url.pathname),
    search: url.search,
    hash: url.hash,
  };
};

const stripPathPrefix = (pathname: string, prefix: string) => {
  const normalizedPath = normalizePathname(pathname);
  const normalizedPrefix = normalizePathname(prefix);

  if (normalizedPrefix === '/') {
    return normalizedPath;
  }

  if (normalizedPath === normalizedPrefix) {
    return '/';
  }

  if (normalizedPath.startsWith(`${normalizedPrefix}/`)) {
    return normalizePathname(normalizedPath.slice(normalizedPrefix.length));
  }

  return normalizedPath;
};

export const getServiceShellPath = (service: Pick<EmbeddedService, 'id'>) => `/${service.id}`;

export const isServiceShellRoute = (
  pathname: string,
  service: Pick<EmbeddedService, 'id'>,
) => {
  const shellPath = getServiceShellPath(service);
  return pathname === shellPath || pathname.startsWith(`${shellPath}/`);
};

export const findIframeServiceForShellPath = (
  services: EmbeddedService[],
  pathname: string,
) => services.find((service) => service.type === 'iframe' && isServiceShellRoute(pathname, service)) ?? null;

export const buildIframeUrlForShellLocation = (
  service: EmbeddedService,
  location: ShellLocationParts,
) => {
  const shellPath = getServiceShellPath(service);
  const appPath = stripPathPrefix(location.pathname, shellPath);
  const base = new URL(service.url || '/', getUrlOrigin());
  const basePath = base.pathname.replace(/\/+$/g, '');
  const appSuffix = appPath === '/' ? '' : appPath.replace(/^\/+/, '');

  base.pathname = appSuffix ? `${basePath || ''}/${appSuffix}` : base.pathname || '/';
  base.search = normalizeSearch(location.search) || base.search;
  base.hash = normalizeHash(location.hash);

  return isAbsoluteUrl(service.url) ? base.toString() : `${base.pathname}${base.search}${base.hash}`;
};

export const buildShellUrlForEmbeddedNavigation = (
  service: EmbeddedService,
  payload: unknown,
) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const navigation = payload as EmbeddedNavigationPayload;
  if (typeof navigation.shellPath === 'string' && navigation.shellPath.trim()) {
    const rawShellPath = navigation.shellPath.trim();
    const parsedShellPath = parseUrlPath(rawShellPath);

    if (isAbsoluteUrl(rawShellPath) && parsedShellPath.origin !== getUrlOrigin()) {
      return null;
    }

    const search = normalizeSearch(navigation.search) || parsedShellPath.search;
    const hash = normalizeHash(navigation.hash) || parsedShellPath.hash;
    return `${parsedShellPath.pathname}${search}${hash}`;
  }

  if (navigation.appId && navigation.appId !== service.id) {
    return null;
  }

  const rawPath = typeof navigation.path === 'string' && navigation.path.trim()
    ? navigation.path.trim()
    : '/';
  const parsedPath = parseUrlPath(rawPath);
  const serviceMountPath = parseUrlPath(service.url || '/').pathname;
  const appPath = stripPathPrefix(
    stripPathPrefix(parsedPath.pathname, serviceMountPath),
    getServiceShellPath(service),
  );
  const search = normalizeSearch(navigation.search) || parsedPath.search;
  const hash = normalizeHash(navigation.hash) || parsedPath.hash;
  const shellPath = getServiceShellPath(service);
  const pathname = appPath === '/' ? shellPath : `${shellPath}${appPath}`;

  return `${pathname}${search}${hash}`;
};

export const shouldPreserveIframeUrlForEmbeddedNavigation = (
  serviceId: string | null,
  embeddedNavigationServiceId: string | null,
  navigationType?: string,
) => Boolean(serviceId && serviceId === embeddedNavigationServiceId && navigationType !== 'POP');
